import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useMarkAllNotificationsReadMutation } from "../../../store/api/notificationsApi";
import { formatDateTime } from "../../../utils/formatDate";

const NotificationCenter = ({ notifications }: { notifications: any[] }) => {
	const [markAllRead, { isLoading: isMarkingAllRead }] =
		useMarkAllNotificationsReadMutation();

	const getNotificationIcon = (type: string) => {
		switch (type) {
			case "payment":
				return "CreditCard";
			case "maintenance":
				return "Wrench";
			case "lease":
				return "FileText";
			case "announcement":
				return "Megaphone";
			default:
				return "Bell";
		}
	};

	const getNotificationColor = (type: string) => {
		switch (type) {
			case "payment":
				return "text-success";
			case "maintenance":
				return "text-warning";
			case "lease":
				return "text-primary";
			case "announcement":
				return "text-purple-500";
			default:
				return "text-muted-foreground";
		}
	};

	const getNotificationBgColor = (type: string) => {
		switch (type) {
			case "payment":
				return "bg-success/10";
			case "maintenance":
				return "bg-warning/10";
			case "lease":
				return "bg-primary/10";
			case "announcement":
				return "bg-purple-500/10";
			default:
				return "bg-muted/10";
		}
	};

	const unreadCount = notifications?.filter((n) => !n.read).length || 0;

	const handleMarkAllRead = async () => {
		try {
			await markAllRead().unwrap();
		} catch (error) {
			console.error("Failed to mark all notifications as read:", error);
		}
	};

	return (
		<div className="bg-card border border-border rounded-lg card-shadow flex flex-col">
			<div className="p-4 border-b border-border flex-shrink-0">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-2">
						<h3 className="font-semibold text-foreground">Notifications</h3>
						{unreadCount > 0 && (
							<span className="px-2 py-1 bg-error text-error-foreground text-xs rounded-full">
								{unreadCount}
							</span>
						)}
					</div>

					{unreadCount > 0 && (
						<Button
							variant="ghost"
							size="sm"
							onClick={handleMarkAllRead}
							disabled={isMarkingAllRead}
							className="text-xs"
						>
							{isMarkingAllRead ? "Marking..." : "Mark as Read"}
						</Button>
					)}
				</div>
			</div>

			<div className="p-4 overflow-y-auto" style={{ maxHeight: "400px" }}>
				{notifications && notifications.length > 0 ? (
					<div className="space-y-3">
						{notifications.map((notification: any) => (
							<div
								key={notification.id}
								className={`p-3 rounded-lg border smooth-transition cursor-pointer ${
									!notification.read
										? "bg-accent/5 border-primary/20 hover:bg-accent/10"
										: "bg-muted/20 border-border hover:bg-muted/30"
								}`}
							>
								<div className="flex items-start space-x-3">
									<div
										className={`p-1.5 rounded ${getNotificationBgColor(
											notification.type,
										)}`}
									>
										<Icon
											name={getNotificationIcon(notification.type)}
											size={14}
											className={getNotificationColor(notification.type)}
										/>
									</div>

									<div className="flex-1 min-w-0">
										<div className="flex items-center justify-between">
											<h4
												className={`text-sm font-medium truncate ${
													!notification.read
														? "text-foreground"
														: "text-muted-foreground"
												}`}
											>
												{notification.title}
											</h4>
											{!notification.read && (
												<div className="w-2 h-2 bg-primary rounded-full ml-2 flex-shrink-0"></div>
											)}
										</div>

										<p className="text-xs text-muted-foreground mt-1 line-clamp-2">
											{notification.message}
										</p>

										<div className="flex items-center justify-between mt-2">
											<span className="text-xs text-muted-foreground">
												{formatDateTime(
													notification.date || notification.created_at,
												)}
											</span>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="text-center py-6">
						<Icon
							name="Bell"
							size={32}
							className="mx-auto mb-3 text-muted-foreground opacity-50"
						/>
						<h4 className="text-sm font-medium text-foreground mb-1">
							No notifications
						</h4>
						<p className="text-xs text-muted-foreground">
							You're all caught up! New notifications will appear here.
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default NotificationCenter;
